import { useRef, useState } from "react";
import { Link } from "@/lib/simple-router";
import { ChevronLeft, ChevronRight, Users, Home, Bed, Bath } from "lucide-react";
import type { Property } from "@shared/schema";
import { sizedImageUrl, sizedImageSrcSet } from "@/lib/img";

// Villa cards render up to ~full container width on mobile and ~1/3 on lg
// listings grid; 1600w candidate keeps Retina screens sharp.
const CARD_IMG_WIDTHS = [800, 1600];
const CARD_IMG_SIZES = "(min-width: 1024px) 30vw, (min-width: 640px) 45vw, 100vw";

interface PropertyCardProps {
  property: Property & {
    heroPhotos?: string[];
    availabilityChecked?: boolean;
    availabilityError?: string;
    pricing?: {
      totalPrice: number;
      averageNightlyRate: number;
      currency: string;
    };
  };
  searchParams?: {
    checkIn?: string;
    checkOut?: string;
    guests?: string;
  };
  isFirstCard?: boolean;
  headingLevel?: 2 | 3;
}

export default function PropertyCard({ property, searchParams, isFirstCard, headingLevel = 3 }: PropertyCardProps) {
  const Heading = headingLevel === 2 ? "h2" : "h3";
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const heroImages = property.heroPhotos?.length ? property.heroPhotos : null;
  const baseImages = property.images.length > 0 ? property.images : ["https://images.unsplash.com/photo-1571896349842-33c89424de2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=600"];
  const images = heroImages || baseImages;
  const gesture = useRef<{ id: number; x: number; y: number; direction: "horizontal" | "vertical" | null } | null>(null);
  const suppressClick = useRef(false);
  
  const nextImage = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };
  
  const prevImage = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  // Build URL with search parameters
  const buildPropertyUrl = () => {
    const baseUrl = `/property/${property.id}`;
    if (!searchParams) {
      return baseUrl;
    }
    
    const params = new URLSearchParams();
    if (searchParams.checkIn) params.append('checkIn', searchParams.checkIn);
    if (searchParams.checkOut) params.append('checkOut', searchParams.checkOut);
    if (searchParams.guests) params.append('guests', searchParams.guests);
    
    return params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;
  };

  return (
    <div className="property-listing mb-6 sm:mb-8 lg:mb-12">
      <Link href={buildPropertyUrl()}>
        <div
          className="relative group cursor-pointer touch-pan-y"
          onPointerDown={(event) => {
            suppressClick.current = false;
            if (event.pointerType !== "touch" || !event.isPrimary || images.length < 2) return;
            gesture.current = { id: event.pointerId, x: event.clientX, y: event.clientY, direction: null };
          }}
          onPointerMove={(event) => {
            const start = gesture.current;
            if (!start || start.id !== event.pointerId) return;
            const dx = Math.abs(event.clientX - start.x);
            const dy = Math.abs(event.clientY - start.y);
            if (!start.direction && Math.max(dx, dy) > 10) {
              start.direction = dx > dy * 1.2 ? "horizontal" : "vertical";
            }
            if (start.direction === "horizontal") suppressClick.current = true;
          }}
          onPointerUp={(event) => {
            const start = gesture.current;
            gesture.current = null;
            if (!start || start.id !== event.pointerId) return;
            const dx = event.clientX - start.x;
            const dy = event.clientY - start.y;
            if (start.direction !== "vertical" && Math.abs(dx) >= 40 && Math.abs(dx) > Math.abs(dy) * 1.2) {
              suppressClick.current = true;
              setCurrentImageIndex(prev => (prev + (dx < 0 ? 1 : images.length - 1)) % images.length);
            }
          }}
          onPointerCancel={() => { gesture.current = null; }}
          onClickCapture={(event) => {
            if (!suppressClick.current || event.detail === 0) return;
            suppressClick.current = false;
            event.preventDefault();
            event.stopPropagation();
          }}
        >
          <img
            src={sizedImageUrl(images[currentImageIndex], 800)}
            srcSet={sizedImageSrcSet(images[currentImageIndex], CARD_IMG_WIDTHS)}
            sizes={CARD_IMG_SIZES}
            alt={`${property.name} - Image ${currentImageIndex + 1}`}
            className="w-full aspect-[4/3] object-cover rounded-lg"
            loading={isFirstCard ? undefined : "lazy"}
            decoding={isFirstCard ? undefined : "async"}
            {...(isFirstCard ? { fetchpriority: "high" } as any : {})}
          />
          
          {/* Navigation arrows - only show if there are multiple images */}
          {images.length > 1 && (
            <>
              {/* Previous arrow */}
              <button
                onClick={prevImage}
                className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-3 sm:p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10"
                aria-label="Previous image"
              >
                <ChevronLeft className="h-5 w-5 sm:h-4 sm:w-4" />
              </button>
              
              {/* Next arrow */}
              <button
                onClick={nextImage}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-3 sm:p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10"
                aria-label="Next image"
              >
                <ChevronRight className="h-5 w-5 sm:h-4 sm:w-4" />
              </button>
            </>
          )}
        </div>
      </Link>
      <div className="pt-3 sm:pt-4">
        <Link href={`/property/${property.id}`}>
          <Heading className="font-semibold text-base sm:text-lg lg:text-xl mb-2 text-toka-text hover:text-toka-primary transition-colors cursor-pointer line-clamp-2">
            {property.name}
          </Heading>
        </Link>
        <p className="text-toka-text text-sm sm:text-base mb-2 line-clamp-2">
          {property.address}
        </p>
        
        <div className="text-xs sm:text-sm text-toka-text">
          <div className="flex items-center flex-wrap gap-x-2 sm:gap-x-3 gap-y-1">
            <span className="flex items-center gap-1 whitespace-nowrap">
              <Users className="h-3 w-3 sm:h-4 sm:w-4" />
              {property.maxGuests} guests
            </span>
            <span className="flex items-center gap-1 whitespace-nowrap">
              <Home className="h-3 w-3 sm:h-4 sm:w-4" />
              {property.bedrooms} bedroom{property.bedrooms !== 1 ? 's' : ''}
            </span>
            <span className="flex items-center gap-1 whitespace-nowrap">
              <Bed className="h-3 w-3 sm:h-4 sm:w-4" />
              {property.bedrooms} bed{property.bedrooms !== 1 ? 's' : ''}
            </span>
            <span className="flex items-center gap-1 whitespace-nowrap">
              <Bath className="h-3 w-3 sm:h-4 sm:w-4" />
              {property.bathrooms} bathroom{property.bathrooms !== "1" ? 's' : ''}
            </span>
          </div>
          
          {/* Availability Status */}
          {property.availabilityChecked !== undefined && (
            <div className="mt-2 pt-2 border-t border-border">
              {property.availabilityChecked ? (
                <div className="flex items-center justify-between">
                  <span className="text-green-600 font-medium text-[16px]">✓ Available</span>
                  {property.pricing && (
                    <span className="text-toka-primary font-semibold text-[16px]">
                      ${property.pricing.totalPrice.toFixed(0)} total
                    </span>
                  )}
                </div>
              ) : property.availabilityError ? (
                <div className="text-yellow-600 font-medium">
                  <span>? Availability unknown</span>
                  <div className="text-xs text-toka-text mt-1">Contact us to confirm</div>
                </div>
              ) : (
                <span className="text-red-600 font-medium">× Not available</span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
